import { makeAutoObservable } from 'mobx'
import { MktuClass, MktuClassWord } from '@/services/Api/generated'
import { MKTU_CLASS_CATEGORY } from '@/models/Mktu'
import { apiService } from '@/services/Api'

export class MktuClassStore {
  selectedWordsNames: MktuClassWord['uuid'][] = []
  words: MktuClassWord[] = []
  loadingWords: boolean = false
  wordsLoaded: boolean = false

  get explanation() {
    return this.mktuClass.explanation
  }

  get included(): string[][] {
    return MktuClassStore.prepareExplanationOptions(this.mktuClass.included)
  }

  get excluded(): string[][] {
    return MktuClassStore.prepareExplanationOptions(this.mktuClass.excluded)
  }

  get wordsCount(): MktuClass['wordsCount'] {
    return this.mktuClass.wordsCount
  }

  get uuid() {
    return this.mktuClass.uuid
  }

  get number() {
    return Number(this.mktuClass.classNumber)
  }

  get description() {
    return this.mktuClass.classDescription
  }

  get type() {
    return this.mktuClass.classNumber <= 34
      ? MKTU_CLASS_CATEGORY.PRODUCT
      : MKTU_CLASS_CATEGORY.SERVICE
  }

  get selectedWords() {
    return this.selectedWordsNames
      .map((name) => {
        return this.words.find((w) => w.word === name)
      })
      .filter((w) => w != null)
  }

  get niceWords() {
    return this.words.filter((word) => word.isNice)
  }

  get totalWordsCount() {
    return this.wordsCount.niceWords + this.wordsCount.notNiceWords
  }

  get hasNotNiceWords() {
    return this.selectedWords.some((w) => !w.isNice)
  }

  constructor(public mktuClass: MktuClass, words?: MktuClassWord[]) {
    this.words = words ?? []
    makeAutoObservable(this, {}, { autoBind: true })
  }

  async getWords() {
    if (this.wordsLoaded || this.loadingWords) {
      return
    }
    this.startLoadingWords()
    try {
      const response = await apiService.mktu.getWords({
        classNumber: this.number,
      })
      if (response.status) {
        this.setWords(response.data)
        this.setWordsLoaded(true)
      }
    } finally {
      this.stopLoadingWords()
    }
  }

  setSelectedWordsNames(names: MktuClassWord['word'][]) {
    this.selectedWordsNames = names
  }

  setWords(words: MktuClassWord[]) {
    this.words = words
  }

  setWordsLoaded(isLoaded: boolean) {
    this.wordsLoaded = isLoaded
  }

  startLoadingWords() {
    this.loadingWords = true
  }

  stopLoadingWords() {
    this.loadingWords = false
  }

  dataForExported() {
    return {
      classNumber: this.number,
      selectedWords: this.selectedWordsNames,
    }
  }

  static prepareExplanationOptions(description: string): string[][] {
    if (description == null || description.length === 0) {
      return []
    }
    return description
      .split('\n')
      .map((i) => i.trim().slice(2))
      .filter((i) => i.length !== 0)
      .map((i) => i.replace(/\(кл. (\d\d)\)/g, '__$$$1__').split('__'))
  }
}
